import React, { useEffect, useRef, useState } from "react";
import { Box, Card, Typography, CardContent, Grid, Backdrop } from "@material-ui/core";
import moment from "moment";
import "moment-timezone";
import { filterBy } from "src/common/utils/helpers";
import TabHeader from "src/components/TabHeader/TabHeader";
import { useStyles } from "./style";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import { useFormik } from "formik";
import { object, string } from "yup";
import { GridRowModel } from "@material-ui/x-grid";
import CommonButton from "src/components/CommonButton/CommonButton";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import CommonLoader from "src/components/Loader/CommonLoader";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonTextField from "src/components/CommonTextField/CommonTextField";
import { filteryByArr, StuffDestuffType } from "src/common/utils/app.dropdowns";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { useDispatch } from "react-redux";

interface Props {}

function mapPendingCheckoutIndex(row: any) {
  if (!row || typeof row !== "object") return row;
  const indexNo = row.indexNo ?? row.indexNumber ?? row.index_number;
  const blNo = row.blNo ?? row.billNumber ?? row.billNo ?? "";
  return {
    ...row,
    indexNo,
    indexNumber: indexNo,
    blNo,
    billNumber: blNo,
    containerNumber: row.containerNo ?? row.containerNumber,
  };
}

function extractPendingCheckoutIndexes(results: any): any[] {
  const raw =
    results?.value?.indexes ??
    results?.indexes ??
    results?.value?.indexesList ??
    results?.value?.list ??
    (Array.isArray(results?.value) ? results.value : []);
  if (!Array.isArray(raw)) return [];

  const seenIndexNos = new Set<string>();
  return raw.map(mapPendingCheckoutIndex).filter((row) => {
    const indexNo = row?.indexNo ?? row?.indexNumber;
    if (indexNo == null) return false;
    const key = String(indexNo);
    if (seenIndexNos.has(key)) return false;
    seenIndexNos.add(key);
    return true;
  });
}

function formatDateTime(value: any) {
  return value ? moment(value).format("DD-MM-YYYY HH:mm") : " ";
}

export function StuffDestuffReport({}: Props): React.ReactElement {
  const columns = [
    {
      accessorKey: "srNo",
      header: "Sr No",
      type: "string",
      size: 80,
      accessorFn: (_row: any, index: number) => index + 1,
    },
    {
      accessorKey: "indexNo",
      header: "Index No",
      type: "string",
      size: 150,
      accessorFn: (row: any) => ` ${row.indexNo || " "}`,
    },
    {
      accessorKey: "containerNumber",
      header: "Container Number",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${row.containerNumber || " "}`,
    },
    {
      accessorKey: "virNo",
      header: "VIR Number",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${row.virNo || " "}`,
    },
    {
      accessorKey: "blNo",
      header: "BL No",
      type: "string",
      size: 180,
      accessorFn: (row: any) => ` ${row.blNo || row.billNumber || " "}`,
    },
    {
      accessorKey: "chassisNumbers",
      header: "Chassis Numbers",
      type: "string",
      size: 180,
      accessorFn: (row: any) => ` ${row.chassisNumbers || " "}`,
    },
    {
      accessorKey: "chassisCheckoutDate",
      header: "Chassis Gate out Date",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${formatDateTime(row.chassisCheckoutDate)}`,
    },
    {
      accessorKey: "indexCheckoutDate",
      header: "Index Gate out Date",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${formatDateTime(row.indexCheckoutDate)}`,
    },
    {
      accessorKey: "containerCheckoutDate",
      header: "Container Gate out Date",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${formatDateTime(row.containerCheckoutDate)}`,
    },
    {
      accessorKey: "description",
      header: "Description",
      type: "string",
      size: 250,
      accessorFn: (row: any) => ` ${row.description || " "}`,
    },
    {
      accessorKey: "destuffDate",
      header: "Destuff Date",
      type: "string",
      size: 250,
      accessorFn: (row: any) => ` ${row.destuffDate || " "}`,
    },
    {
      accessorKey: "markAndNumbers",
      header: "Mark & Numbers",
      type: "string",
      size: 250,
      accessorFn: (row: any) => ` ${row.markAndNumbers || " "}`,
    },
    {
      accessorKey: "consignee",
      header: "Consignee",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${row.consignee || " "}`,
    },
    {
      accessorKey: "vessel",
      header: "Vessel",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${row.vessel || " "}`,
    },
    {
      accessorKey: "arrivalDate",
      header: "Arrival Date",
      type: "string",
      size: 200,
      accessorFn: function customCell(row: any) {
        return `${row.arrivalDate ? moment(row.arrivalDate).format("DD-MM-YYYY") : ""}`;
      },
    },
    {
      accessorKey: "sealNumber",
      header: "Seal Number",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${row.sealNumber || " "}`,
    },
  ];

  const destuffOnlyColumnKeys = [
    "markAndNumbers",
    "blNo",
    "chassisNumbers",
    "chassisCheckoutDate",
    "indexCheckoutDate",
    "containerCheckoutDate",
  ];

  const getDisplayColumns = (reportType: string) =>
    reportType === "Stuff"
      ? columns.filter((col) => !destuffOnlyColumnKeys.includes(col.accessorKey))
      : columns;

  const classes = useStyles();

  const moduleName = "Stuff/Destuff Report";
  const pageHeading = "Stuff/Destuff Report";
  const [displayColumns, setDisplayColumns] = React.useState<any>(getDisplayColumns("Stuff"));
  const [gridRecord, setGridRecord] = React.useState<GridRowModel[]>([]);
  const [loader, setLoader] = useState<any>(false);
  const isExportString = localStorage.getItem("isAllowToExportData");
  const isExport = isExportString === "true";
  const [isCustomDate, setIsCustomDate] = useState<boolean>(false);
  const [virOptions, setVirOptions] = useState<any[]>([]);
  const [virIndexOptions, setVirIndexOptions] = useState<any[]>([]);
  const [virInputLength, setVirInputLength] = useState(0);
  const startDatePickerRef: any = useRef(null);
  const endDatePickerRef: any = useRef(null);
  const dispatch = useDispatch();

  const validationSchema = object({
    startDate: string().when("filterBy", {
      is: (filterBy: string | null) => Boolean(filterBy),
      then: string().required("This field is required."),
      otherwise: string().nullable(),
    }),
    endDate: string().when("filterBy", {
      is: (filterBy: string | null) => Boolean(filterBy),
      then: string().required("This field is required."),
      otherwise: string().nullable(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      filterBy: "today",
      startDate: moment().startOf("day").format("YYYY-MM-DDTHH:mm"),
      endDate: moment().endOf("day").format("YYYY-MM-DDTHH:mm"),
      reportType: "Stuff",
      virNumber: "",
      indexNumber: "",
      billNumber: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const body: Record<string, any> = {
        startDate: values.filterBy ? values.startDate || null : null,
        endDate: values.filterBy ? values.endDate || null : null,
        reportType: values.reportType || null,
      };
      if (values.reportType === "Destuff") {
        body.virNo = values.virNumber?.trim() || null;
        body.indexNo = values.indexNumber?.trim() || null;
        body.billNumber = values.billNumber?.trim() || null;
      }
      setGridRecord([]);
      setLoader(true);
      try {
        const results = await fetchWrapper.post("get-stuff-and-destuff-report", body);
        setGridRecord(results?.value?.report || []);
        setDisplayColumns(getDisplayColumns(values.reportType));
      } catch (error: any) {
        setLoader(false);
        dispatch(
          addNotificationAction({
            message: error.message || "Error fetching report data",
            options: { variant: "error" },
          })
        );
      }
      setLoader(false);
    },
  });

  const exportHandler = async (rows: any[]) => {
    dispatch(
      addNotificationAction({
        message: "Your download will begin shortly",
        options: { variant: "success" },
      })
    );

    const exportedRecords = rows.map((data: any) => {
      const baseRecord = {
        "Index No": data?.indexNo || "",
        "Container Number": data?.containerNumber || "",
        "VIR Number": data?.virNo || "",
        Description: data?.description || "",
        Consignee: data?.consignee || "",
        Vessel: data?.vessel || "",
        "Arrival Date": data?.arrivalDate ? moment(data?.arrivalDate).format("DD-MM-YYYY") : "",
        "Seal Number": data?.sealNumber || "",
      };

      if (formik.values.reportType === "Destuff") {
        return {
          ...baseRecord,
          "BL No": data?.blNo || data?.billNumber || "",
          "Chassis Numbers": data?.chassisNumbers || "",
          "Chassis Gate out Date": formatDateTime(data?.chassisCheckoutDate),
          "Index Gate out Date": formatDateTime(data?.indexCheckoutDate),
          "Container Gate out Date": formatDateTime(data?.containerCheckoutDate),
          "Destuff Date": data?.destuffDate || "",
          "Mark & Numbers": data?.markAndNumbers || "",
        };
      }

      return baseRecord;
    });

    return exportedRecords;
  };

  const filterByAction = (data: any) => {
    if (!data || !data.value) {
      formik.setFieldValue("filterBy", null);
      formik.setFieldValue("startDate", "");
      formik.setFieldValue("endDate", "");
      setIsCustomDate(false);
      return;
    }
    formik.setFieldValue("filterBy", data.value);
    const res = filterBy(data);
    if (res?.isCustomDate) {
      setIsCustomDate(true);
      formik.setFieldValue("startDateCustom", res?.startDate);
      formik.setFieldValue("endDateCustom", res?.endDate);
    } else {
      setIsCustomDate(false);
    }
    formik.setFieldValue("startDate", res?.startDate);
    formik.setFieldValue("endDate", res?.endDate);
  };

  const applyReportTypeDefaults = (reportType: string) => {
    if (reportType === "Destuff") {
      formik.setFieldValue("filterBy", null);
      formik.setFieldValue("startDate", "");
      formik.setFieldValue("endDate", "");
      setIsCustomDate(false);
      return;
    }
    const todayFilter = filteryByArr.find(({ value }) => value === "today");
    if (todayFilter) {
      filterByAction(todayFilter);
    } else {
      formik.setFieldValue("filterBy", "today");
    }
  };

  const getVirNumbers = async (virNo: string) => {
    const trimmed = String(virNo ?? "").trim();
    if (!trimmed) return;
    const results: any = await fetchWrapper.get(`search-vir-number/${trimmed}`);
    if (results?.isSuccess) setVirOptions(results.value?.virList ?? []);
  };

  const getPendingCheckoutIndexes = async (vir: string) => {
    const trimmed = String(vir ?? "").trim();
    if (!trimmed) {
      setVirIndexOptions([]);
      return;
    }
    try {
      const results: any = await fetchWrapper.get(
        `get-pending-checkout-indexes?virNo=${encodeURIComponent(trimmed)}`
      );
      setVirIndexOptions(extractPendingCheckoutIndexes(results));
    } catch {
      setVirIndexOptions([]);
    }
  };

  const resetSearch = () => {
    formik.resetForm();
    setGridRecord([]);
    setVirOptions([]);
    setVirIndexOptions([]);
    setVirInputLength(0);
    formik.setFieldValue("filterBy", "today");
    formik.setFieldValue("startDate", moment().startOf("day").format("YYYY-MM-DDTHH:mm"));
    formik.setFieldValue("endDate", moment().endOf("day").format("YYYY-MM-DDTHH:mm"));
    formik.setFieldValue("reportType", "Stuff");
    setDisplayColumns(getDisplayColumns("Stuff"));
  };

  const fetchData = async () => {
    const body = {
      startDate: moment(new Date(new Date().setDate(new Date().getDate())).setHours(0, 0)).format(
        "YYYY-MM-DDTHH:mm"
      ),
      endDate: moment(new Date(new Date().setDate(new Date().getDate())).setHours(23, 59)).format(
        "YYYY-MM-DDTHH:mm"
      ),
      reportType: formik.values.reportType || "Stuff",
    };
    setGridRecord([]);
    setLoader(true);
    try {
      const results = await fetchWrapper.post("get-stuff-and-destuff-report", body);
      setGridRecord(results?.value?.report || []);
      setDisplayColumns(getDisplayColumns(formik.values.reportType || "Stuff"));
    } catch (error: any) {
      setLoader(false);
      dispatch(
        addNotificationAction({
          message: error.message || "Error fetching report data",
          options: { variant: "error" },
        })
      );
    }
    setLoader(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <TabHeader module={moduleName} heading={pageHeading} />
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>

      <Box m={2} />
      <Card>
        <CardContent className={classes.cardMain}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item md={9}>
              <Box mt={1} mb={2}>
                <Typography className={classes.pageHeading}> {pageHeading} </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3} direction="row">
              <Box textAlign="right" mt={1} mb={1}>
                <CommonButton cssClass="customSubmitBtn" onClick={() => resetSearch()}>
                  Reset
                </CommonButton>
                <CommonButton
                  cssClass="customSubmitBtn"
                  isGreen
                  onClick={() => formik.submitForm()}
                >
                  Search
                </CommonButton>
              </Box>
            </Grid>
          </Grid>

          <>
            <Grid spacing={1} container direction="row">
              <Grid item xs={12} md={3} lg={2}>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  error={formik.touched.filterBy && Boolean(formik.errors.filterBy)}
                  helperText={formik.touched.filterBy && formik.errors.filterBy}
                  label="Filter by"
                  placeholder="Filter by"
                  name="filterBy"
                  disableClearable={formik.values.reportType !== "Destuff"}
                  optionsType={filteryByArr}
                  getOptionLabel={(option: any) => option.label || null}
                  onChange={(row: any) => {
                    if (row) {
                      formik.setFieldValue("filterBy", row.value || "");
                    }
                    filterByAction(row);
                  }}
                  value={
                    formik.values.filterBy
                      ? filteryByArr.find(({ value }: any) => value === formik.values.filterBy) ||
                        null
                      : null
                  }
                />
              </Grid>
              <Grid item xs={12} md={3} lg={2}>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Report Type"
                  name="reportType"
                  placeholder="Select Type"
                  disableClearable={true}
                  optionsType={StuffDestuffType}
                  getOptionLabel={(option: any) => option || ""}
                  onChange={(row: any) => {
                    const reportType = row || "Stuff";
                    formik.setFieldValue("reportType", reportType);
                    formik.setFieldValue("virNumber", "");
                    formik.setFieldValue("indexNumber", "");
                    formik.setFieldValue("billNumber", "");
                    setVirOptions([]);
                    setVirIndexOptions([]);
                    setVirInputLength(0);
                    applyReportTypeDefaults(reportType);
                    setDisplayColumns(getDisplayColumns(reportType));
                  }}
                  value={
                    StuffDestuffType.find((value: any) => value === formik.values.reportType) || ""
                  }
                />
              </Grid>
              {formik.values.reportType === "Destuff" && (
                <>
                  <Grid item xs={12} md={3} lg={2}>
                    <CommonAutoCompleteField
                      cssClassName="customTextField"
                      label="VIR Number"
                      placeholder="Search VIR Number"
                      name="virNumber"
                      optionsType={virOptions}
                      getOptionLabel={(option: any) => option?.virNo || ""}
                      value={
                        virOptions.find((x: any) => x?.virNo === formik.values.virNumber) || null
                      }
                      handleChange={(v: string) => formik.setFieldValue("virNumber", v)}
                      onChange={(option: any) => {
                        if (!option) {
                          formik.setFieldValue("virNumber", "");
                          formik.setFieldValue("indexNumber", "");
                          formik.setFieldValue("billNumber", "");
                          setVirIndexOptions([]);
                          return;
                        }
                        formik.setFieldValue("virNumber", option?.virNo || "");
                        formik.setFieldValue("indexNumber", "");
                        formik.setFieldValue("billNumber", "");
                        getPendingCheckoutIndexes(option?.virNo);
                      }}
                      onKeyUp={(e: any) => {
                        const nextLen = e.target.value.length;
                        setVirInputLength(nextLen);
                        if (nextLen === 2 && virInputLength < 2) getVirNumbers(e.target.value);
                      }}
                      onKeyDownCapture={(e: any) => {
                        if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                      }}
                      onPaste={(e: any) => {
                        const pasted = e.clipboardData.getData("text");
                        if (/[^A-Za-z0-9\d]/.test(pasted)) {
                          e.preventDefault();
                          return;
                        }
                        if (pasted.length >= 2) getVirNumbers(pasted);
                      }}
                    />
                  </Grid>
                  {formik.values.virNumber && (
                    <Grid item xs={12} md={3} lg={2}>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        label="Index Number"
                        placeholder="Select Index Number"
                        name="indexNumber"
                        optionsType={virIndexOptions}
                        getOptionLabel={(option: any) =>
                          String(option?.indexNo ?? option?.indexNumber ?? "")
                        }
                        value={
                          virIndexOptions.find((x: any) => {
                            const idx = x?.indexNo ?? x?.indexNumber ?? "";
                            return String(idx) === String(formik.values.indexNumber);
                          }) || null
                        }
                        onChange={(option: any) => {
                          formik.setFieldValue(
                            "indexNumber",
                            option ? String(option?.indexNo ?? option?.indexNumber ?? "") : ""
                          );
                          formik.setFieldValue(
                            "billNumber",
                            option ? String(option?.blNo ?? option?.billNumber ?? "") : ""
                          );
                        }}
                      />
                    </Grid>
                  )}
                  {formik.values.indexNumber && (
                    <Grid item xs={12} md={3} lg={2}>
                      <CommonTextField
                        label="BL Number"
                        name="billNumber"
                        value={formik.values.billNumber}
                        isReadOnly
                      />
                    </Grid>
                  )}
                </>
              )}
              <Grid
                item
                xs={12}
                md={3}
                lg={2}
                className={!isCustomDate ? classes.hideDatesGrid : ""}
              >
                <CommonTextFieldDate
                  cssClassName="customTextField"
                  label={"Start Date"}
                  name={"startDate"}
                  datePickerRef={startDatePickerRef}
                  value={formik.values.startDate}
                  onChange={(e: any) => {
                    formik.setFieldValue("startDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                    formik.setFieldValue("endDate", "");
                  }}
                  maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
                lg={2}
                className={!isCustomDate ? classes.hideDatesGrid : ""}
              >
                <CommonTextFieldDate
                  cssClassName="customTextField"
                  label={"End Date"}
                  name={"endDate"}
                  datePickerRef={endDatePickerRef}
                  value={formik.values.endDate}
                  onChange={(e: any) => {
                    formik.setFieldValue("endDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                  }}
                  minDate={formik.values.startDate}
                  maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                />
              </Grid>
            </Grid>
          </>
          <CommonMaterialGrid
            key={formik.values.reportType}
            rows={gridRecord}
            columns={displayColumns}
            defaultPerPageRecord={15}
            loader={loader}
            height={550}
            isExport={isExport}
            exportHandler={exportHandler}
            filename={`Stuff_Destuff_report_${Date.now()}`}
            isFooter={true}
          />
        </CardContent>
      </Card>
    </>
  );
}
